/* eslint-disable max-len */
/* eslint-disable no-restricted-syntax */
/**
 * Utility function to concat classNames.
 *
 * Usage: classnames('css_class1', 'css_class1')
 *
 * Can be used with objects where the keys are css classes and the
 * values are booleans that decide if classes are active or not:
 *
 * Example: classnames('input', { 'input-error': has_errors })
 *
 * @param  {...any} args
 * @returns string
 */
import { ContentIcons } from 'assets/icons/content-icons';
import {
  VaultTabs,
  AlbumContentType,
  QuestionsManagmentViewType,
  RecordingStatus,
  TimeUnit,
  UserTypeId,
} from 'common/enums';
import { screenSizes } from 'config/constants';
import { useMediaQuery } from 'hooks/use-media-query';
import { Album } from 'models/album';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { goToPage, RouteName } from 'routes';

type ClassnameObject = {
  [key: string]: string | boolean | number;
};

type Classname = ClassnameObject | string;

type HTMLValidationError = {
  [n: string]: string;
};

function classnames(...args: Classname[]): string {
  if (args.length === 1) {
    const [firstEntry] = args;
    if (firstEntry && typeof firstEntry === 'object') {
      /* firstEntry's keys whose value is truthy */
      const activeClasses = Object.entries(firstEntry)
        .filter(([, value]) => value)
        .map(([key]) => key);
      return activeClasses.join(' ');
    }
    return firstEntry;
  }
  return args
    .filter((entry) => !!entry)
    .map((value) => classnames(value))
    .join(' ');
}

const getQueryParamsObject = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const queryParamsObject: any = {};

  queryParams.forEach((value, key) => {
    queryParamsObject[key] = value;
  });

  return queryParamsObject;
};

const getQueryParam = (param: string) => {
  const prop = new URLSearchParams(window.location.search).get(param);

  return prop || null;
};

const checkValueAndGetEmptyError = (value: string) =>
  value.trim() === '' ? "shouldn't be empty" : '';

const isEmailValid = (emailAddress: string) => {
  const re =
    /[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?/;
  return emailAddress && re.test(emailAddress);
};

const isPasswordValid = (password: string) => {
  if (
    password.length < 8 ||
    !/[a-zA-Z]/.test(password) ||
    !/[0-9]/.test(password)
  )
    return false;

  return true;
};

const noEmptyFields = (
  data: MandatoryFieldsType,
  mandatoryFields?: MandatoryFieldsType,
) => {
  if (mandatoryFields) {
    for (const field in mandatoryFields) {
      if (!data[field] || data[field] === '') return false;
    }
  } else {
    for (const field in data) {
      if (data[field] === '') return false;
    }
  }

  return true;
};

const noErrors = (errors: MandatoryFieldsType) => {
  for (const field in errors) {
    if (errors[field] !== '') return false;
  }

  return true;
};

const isValidForm = (
  data: MandatoryFieldsType,
  mandatoryFields: MandatoryFieldsType,
  errors: MandatoryFieldsType,
) => noEmptyFields(data, mandatoryFields) && noErrors(errors);

const isAnyLoggedUser = (userLogged: UserType) => userLogged.id > 0;

const isOwner = (
  userLogged: UserType,
  vault: VaultType | VaultCollaboratorsType,
) => isAnyLoggedUser(userLogged) && userLogged.id === vault.owner.id;

const isContributor = (userLogged: UserType, vault: VaultType) =>
  isAnyLoggedUser(userLogged) &&
  !!vault.contributors.find((contributor) => contributor.id === userLogged.id);

const isGuest = (userLogged: UserType, vault: VaultType) =>
  isAnyLoggedUser(userLogged) &&
  !!vault.guests.find((contributor) => contributor.id === userLogged.id);

const hasVaultAccess = (userLogged: UserType, vault: VaultType) =>
  isOwner(userLogged, vault) || isContributor(userLogged, vault);

const isOwnerOrClipOwner = (
  userLogged: UserType,
  vault: VaultType,
  clip: ClipType,
) =>
  isAnyLoggedUser(userLogged) &&
  (userLogged.id === vault.owner.id ||
    !clip.createdByOwner ||
    clip.recordedBy === userLogged.id);

const calculateHoursAndMinutes = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return { hours, minutes };
};

const getSortedClipsByRecordedAt = (clips: ClipType[]) =>
  clips.sort(
    (a: ClipType, b: ClipType) =>
      +new Date(a.recordedAt) - +new Date(b.recordedAt),
  );

const showTextEllipsis = (text: string, maxCharacteres: number) => {
  if (text.length < maxCharacteres) return text;

  return `${text.substring(0, maxCharacteres)}...`;
};

// Rejects when the browser refuses clipboard access, so callers that show a
// confirmation should await it.
const copyToClipboard = async (copyMe: string) => {
  await navigator.clipboard.writeText(copyMe);
};

const hasNotUserValidatedEmail = (user: UserType) => user.verified === false;

const getAllMediaQueries = () => {
  const result: Partial<MediaQueriesType> = {};

  Object.keys(screenSizes).forEach((size) => {
    result[size as keyof typeof screenSizes] = useMediaQuery(
      screenSizes[size as keyof typeof screenSizes],
    );
  });

  return result as MediaQueriesType;
};

const dateTimePassedIsLessThan = (date: Date, milliseconds: number) =>
  new Date().getTime() - new Date(date).getTime() < milliseconds;

const getIndexClipSelectedInList = (
  clipsList: ClipType[],
  actualClipSelected: ClipType,
) => clipsList.findIndex((clip: ClipType) => clip.id === actualClipSelected.id);

const isNotEmptyAndInvalidEmail = (email: string) =>
  email && !isEmailValid(email);

const checkEmailAndGetGenericError = (email: string) =>
  isNotEmptyAndInvalidEmail(email) ? 'Invalid email' : '';

const checkStateAndGetError = (state: string) =>
  state === '' ? 'You must select a state' : '';

const getPriceInfoBySubscription = (
  pricesBySubscription: SubscriptionDiscountedPriceType[],
  subSelected: SubscriptionTypeType,
): SubscriptionDiscountedPriceType =>
  pricesBySubscription.find((priceSub) => priceSub.id === subSelected.id)!;

const anyClipInProcess = (unansweredQuestions?: ClipType[]) => {
  if (!unansweredQuestions) return false;

  return unansweredQuestions.some(
    (clip: ClipType) => clip.recordingStatus === RecordingStatus.processing,
  );
};

const isFreeTrial = (vault: VaultType | VaultCollaboratorsType) =>
  vault.subscriptionType === 'trial';

const isReadOnly = (vault: VaultType) =>
  vault.subscriptionType === 'read_only' ||
  vault.subscriptionType === 'read_only_free';

const isLegacy = (vault: VaultType) => vault.subscriptionType === 'legacy';

const isLifeTime = (vault: VaultType) =>
  vault.subscriptionType === 'lifetime_album';

const isRestrictedVault = (vault: VaultType | VaultCollaboratorsType) =>
  (!isFreeTrial(vault) && vault.subscriptionStatus === 'payment_failed') ||
  (!isFreeTrial(vault) && vault.subscriptionStatus === 'subscription_overdue');

const isReadOnlySubOrRestrictedVault = (vault: VaultType) =>
  isReadOnly(vault) || isRestrictedVault(vault);

const isCanceledVault = (vault: VaultType) =>
  !isFreeTrial(vault) && vault.subscriptionStatus === 'subscription_canceled';

const isLockedVault = (vault: VaultType | VaultCollaboratorsType) =>
  !isFreeTrial(vault) && vault.subscriptionStatus === 'subscription_ended';

const totalVaultVideoClips = (vault: VaultType) => vault.clips?.length || 0;

const hasContributor = (vault: VaultType) => vault.contributors.length;

const getSubscriptionSchemeById = (
  subscriptionsScheme: SubscriptionTypeType[],
  id: SubscriptionSchemeId,
) =>
  subscriptionsScheme.find(
    (subcription: SubscriptionTypeType) => subcription.id === id,
  );

// TODO: Deprecate this function since the max clips amount comes in the vault now
const getMaxQuestionsAllowedByVault = (
  vault: VaultType | VaultCollaboratorsType,
  subsScheme: SubscriptionTypeType[],
): number | undefined => {
  const scheme = getSubscriptionSchemeById(subsScheme, vault.subscriptionType);

  return scheme?.permissions?.clipNumbers;
};

const getClipPriceByVault = (
  vault: VaultType,
  subsScheme: SubscriptionTypeType[],
): number => {
  const scheme = getSubscriptionSchemeById(subsScheme, vault.subscriptionType);

  return scheme?.extraClipPrice ?? 0;
};

const getExtraCallTimeHourPriceVault = (
  vault: VaultType,
  subsScheme: SubscriptionTypeType[],
): number => {
  const scheme = getSubscriptionSchemeById(subsScheme, vault.subscriptionType);

  return scheme?.extraVideoCallTimePrice ?? 0;
};

const getPermissionsByVault = (
  subsScheme: SubscriptionTypeType[],
  vault: VaultType,
): SubscriptionPermissionsType => {
  const scheme = getSubscriptionSchemeById(subsScheme, vault.subscriptionType);

  return scheme!.permissions!;
};

const hasVaultReachedLimitMessage = (
  vault: VaultType,
  tab: VaultTabs,
): string => {
  if (
    [VaultTabs.clips, VaultTabs.addPrompts, VaultTabs.videoCall].includes(tab)
  ) {
    const fullMessage = isFreeTrial(vault)
      ? 'Your trial vault is full'
      : 'Your vault is full';
    const reachingLimitMessage = isFreeTrial(vault)
      ? 'Your trial vault is almost full'
      : 'Your vault is almost full';

    if (totalVaultVideoClips(vault) >= vault.maxClips) {
      return fullMessage;
    }
    if (vault.maxClips - totalVaultVideoClips(vault) <= 3) {
      return reachingLimitMessage;
    }
  }

  return '';
};

const convertSecondsToTimeUnit = (
  totalSeconds: number,
  timeUnit: TimeUnit,
): number => {
  if (timeUnit === TimeUnit.milliseconds) {
    return totalSeconds * 1000;
  }

  if (timeUnit === TimeUnit.minutes) {
    return Math.floor(totalSeconds / 60);
  }

  if (timeUnit === TimeUnit.hours) {
    return Math.floor(totalSeconds / (60 * 60));
  }

  return totalSeconds;
};

const redictDependingFailingStatus = (user: UserType) => {
  if (hasNotUserValidatedEmail(user)) {
    goToPage(RouteName.EmailWaitingConfirm);
    return true;
  }

  return false;
};

const capitalizeFirstLetter = (word: string) =>
  word.charAt(0).toUpperCase() + word.slice(1);

const getRoleDisplay = (type: CollaboratorType): string => {
  if (type === 'contributor') return 'Editor';
  if (type === 'guest') return 'Contributor';
  return capitalizeFirstLetter(type);
};

const getVaultStatusLabel = (vault: VaultType) => {
  let withIcon = true;
  let statusValue = null;

  // A draft whose Checkout wasn't paid; opening it offers to finish paying.
  if (vault.subscriptionStatus === 'awaiting_checkout') {
    withIcon = false;
    statusValue = 'Payment pending';
  } else if (isRestrictedVault(vault)) {
    statusValue = 'Restricted';
  } else if (isCanceledVault(vault)) {
    statusValue = 'Canceled';
  } else if (isLockedVault(vault)) {
    statusValue = 'Locked';
  } else if (isReadOnly(vault)) {
    withIcon = false;
    statusValue = 'Read only';
  } else if (isFreeTrial(vault)) {
    withIcon = false;
    statusValue = 'Trial';
  }

  return { withIcon, statusValue };
};

// Check if the number, order, or names of questions have changed
const questionsHaveChanged = (
  originalQuestions: QuestionType[],
  updatedQuestions: QuestionType[],
) => {
  if (originalQuestions.length !== updatedQuestions.length) return true;
  for (let i = 0; i < originalQuestions.length; i += 1) {
    if (originalQuestions[i].name !== updatedQuestions[i].name) return true;
  }
  return false;
};

const isEditingVault = (questionsManagmentView: QuestionsManagmentViewType) =>
  questionsManagmentView === QuestionsManagmentViewType.editVault;

const isEditingMyTemplates = (
  questionsManagmentView: QuestionsManagmentViewType,
) => questionsManagmentView === QuestionsManagmentViewType.myTemplates;

// Every vault has one hidden "default" album holding the memories the user
// didn't file anywhere. It must never appear in an album list or picker — only
// its contents surface, mixed into the memory lists.
const excludeDefaultAlbum = <T extends { isDefault?: boolean }>(
  albums: T[],
): T[] => albums.filter((album) => !album.isDefault);

// An album's name as the user sees it: the hidden default album is "Without
// Album".
const albumDisplayName = (album: { name?: string; isDefault?: boolean }) =>
  album.isDefault ? 'Without Album' : album.name || 'Untitled album';

// Block body rather than a concise one, so the executor doesn't return
// setTimeout's timer id (no-promise-executor-return).
const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const isSpecificBrowserVersion = (browser: string, version: string) =>
  // TODO: Typescript doesn't support `userAgentData` yet since it is still experimental
  // @ts-ignore
  window.navigator.userAgentData?.brands.find(
    (brand: { brand: string; version: any }) =>
      brand.brand.includes(browser) &&
      Number(brand.version) === Number(version),
  );

const isLowerBrowserVersion = (browser: string, version: string) =>
  // TODO: Typescript doesn't support `userAgentData` yet since it is still experimental
  // @ts-ignore
  window.navigator.userAgentData?.brands.find(
    (brand: { brand: string; version: any }) =>
      brand.brand.includes(browser) && Number(brand.version) < Number(version),
  );

// INFO: Includes Chrome & Chromium
const isChromeVersionWithIssues = () =>
  isSpecificBrowserVersion('Chrom', '115');

const formatRecordingTime = (time: number) => {
  const timeInSec = time % 60;
  const mins = Math.floor(time / 60);

  const timeInSecStr = timeInSec < 10 ? `0${timeInSec}` : timeInSec;
  const minsStr = mins < 10 ? `0${mins}` : mins;

  return `${minsStr}:${timeInSecStr}`;
};

const getClipNameOrVaultTitle = (clipId: number, vault: VaultType) => {
  if (vault.clips) {
    const clipName = vault.clips.find((clip: ClipType) => clip.id === clipId);
    if (clipName) return clipName.name;
  }
  return vault.title;
};

const canUpgradeVault = (
  vault: VaultType,
  subscriptionsScheme?: SubscriptionTypeType[],
) => {
  if (!subscriptionsScheme) return false;
  const permissions = getPermissionsByVault(subscriptionsScheme, vault);
  return permissions && !!permissions.canSwitchTo;
};

const canBuyClips = (
  vault: VaultType,
  subscriptionsScheme?: SubscriptionTypeType[],
) => {
  if (!subscriptionsScheme) return false;
  const permissions = getPermissionsByVault(subscriptionsScheme, vault);
  return permissions && !!permissions.buyExtraClips;
};

const canBuyExtraVideoCallTime = (
  vault: VaultType,
  subscriptionsScheme?: SubscriptionTypeType[],
) => {
  if (!subscriptionsScheme) return false;
  const permissions = getPermissionsByVault(subscriptionsScheme, vault);
  return permissions && !!permissions.buyExtraVideoCallTime;
};

const formatProgressBarTime = (hours: number, minutes: number) => {
  let text = '';
  if (hours > 0 || minutes === 0) {
    text = `${text} ${hours} ${hours > 1 ? 'hours' : 'hour'}`;
  }
  if (minutes > 0) {
    text = `${text}${text.length > 0 ? ', ' : ''}${minutes} ${
      minutes > 1 ? 'minutes' : 'minute'
    }`;
  }
  return text;
};

const createDateObject = (
  year: number,
  month: number | undefined,
  day: number | undefined,
) => new Date(year, month ? month - 1 : 11, day || 31);

const isExpiredDate = (date: Date) => new Date() > date;

const formatPrice = (price: number) => {
  const number = parseFloat(price.toString());
  if (number % 1 !== 0) {
    return number.toFixed(2);
  }
  return number;
};

const vaultHasPublicClip = (alb: VaultType) => {
  if (!alb?.clips?.length) return false;

  return alb.clips.find((clip: ClipType) => !clip.isPrivate);
};

const isTemporaryUser = (user: UserType) =>
  user.permissions?.id === UserTypeId.Temporary;

const userPermissionUpgrade = (user: UserType) =>
  !!user.permissions?.permissions.upgradeVaults;

const getInitials = (name: string) => {
  if (!name || typeof name !== 'string' || name.trim() === '') {
    return 'N/A';
  }
  const words = name.trim().split(' ');
  const firstInitial = words[0][0];
  if (words.length === 1) {
    return firstInitial.toUpperCase();
  }
  const lastInitial = words[words.length - 1][0] || '';
  return `${firstInitial} ${lastInitial}`.toUpperCase();
};

type SplitDateResult = {
  year: number;
  month?: number;
  day?: number;
};

const splitDateString = (dateString: string): SplitDateResult => {
  const parts = dateString.split('-').map((p) => p.trim());
  const [yearStr, monthStr, dayStr] = parts;

  const year = parseInt(yearStr, 10);
  const month = monthStr ? parseInt(monthStr, 10) : undefined;
  const day = dayStr ? parseInt(dayStr, 10) : undefined;

  return { year, month, day };
};

const formatDate = (dateString: SplitDateResult) => {
  const options: Intl.DateTimeFormatOptions = {};
  if (dateString.month !== undefined) options.month = 'long';
  if (dateString.day !== undefined) options.day = 'numeric';
  options.year = 'numeric';

  return createDateObject(
    dateString.year,
    dateString.month,
    dateString.day,
  ).toLocaleDateString('en-US', options);
};

function getDateRange(
  startDate?: string,
  endDate?: string | null,
  getFormattedDate?: true,
): { startDate: string; endDate: string };

function getDateRange(
  startDate?: string,
  endDate?: string | null,
  getFormattedDate?: false,
): { startDate: SplitDateResult; endDate: SplitDateResult };

function getDateRange(
  startDate?: string,
  endDate?: string | null,
  getFormattedDate: boolean = true,
):
  | { startDate: string; endDate: string }
  | { startDate: SplitDateResult; endDate: SplitDateResult } {
  if (!startDate || !endDate) return { startDate: '', endDate: '' };
  let start: SplitDateResult = splitDateString(startDate);
  let end: SplitDateResult = splitDateString(endDate);
  const startFullYear =
    start.day === 1 &&
    start.month === 1 &&
    (start.year < end.year ||
      (start.year === end.year && end.month === 12 && end.day === 31));

  const endFullYear =
    end.day === 31 &&
    end.month === 12 &&
    (end.year > start.year ||
      (end.year === start.year && start.month === 1 && start.day === 1));

  if (startFullYear) start = { year: start.year };
  if (endFullYear) end = { year: end.year };

  if (startFullYear && endFullYear)
    if (getFormattedDate) {
      return {
        startDate: formatDate(start),
        endDate: formatDate(end),
      };
    } else {
      return {
        startDate: start,
        endDate: end,
      };
    }

  const startFullMonth =
    !startFullYear &&
    start.day === 1 &&
    (endFullYear ||
      end.month !== start.month ||
      (end.month === start.month && end.day === 31));

  const endFullMonth =
    !endFullYear &&
    end.day === 31 &&
    (startFullYear ||
      start.month !== end.month ||
      (start.month === end.month && start.day === 1));

  if (startFullMonth) start = { year: start.year, month: start.month };
  if (endFullMonth) end = { year: end.year, month: end.month };

  if (startFullMonth && endFullMonth)
    if (getFormattedDate) {
      return {
        startDate: formatDate(start),
        endDate: formatDate(end),
      };
    } else {
      return {
        startDate: start,
        endDate: end,
      };
    }

  if (!startFullMonth)
    start = { year: start.year, month: start.month, day: start.day };

  if (!endFullMonth) end = { year: end.year, month: end.month, day: end.day };

  if (getFormattedDate) {
    return {
      startDate: formatDate(start),
      endDate: formatDate(end),
    };
  }
  return {
    startDate: start,
    endDate: end,
  };
}

const createDateRange = (
  startDate: { year: number; month: number; day: number },
  endDate: { year: number; month: number; day: number },
  isSpecificDate: boolean,
) => {
  if (isSpecificDate)
    return {
      startDate: createDateObject(
        startDate.year,
        startDate.month || 1,
        startDate.day || 1,
      ),
      endDate: createDateObject(
        startDate.year,
        startDate.month || 12,
        startDate.day || 31,
      ),
    };
  return {
    startDate: createDateObject(
      startDate.year,
      startDate.month || 1,
      startDate.day || 1,
    ),
    endDate: createDateObject(
      endDate.year,
      endDate.month || 12,
      endDate.day || 31,
    ),
  };
};

const adaptContentType = (content: AlbumContentSerialized) => {
  if (
    content.contentType === AlbumContentType.image ||
    content.contentType === AlbumContentType.write ||
    content.contentType === AlbumContentType.video
  )
    return content.contentType;
  // A recording made audio-only (recordings have no file type of their own).
  // Uploads go by their file type below: uploaded videos are saved as "audio"
  // memories too, and must stay videos.
  if (
    content.contentType === AlbumContentType.audio &&
    !content.content.contentType
  )
    return AlbumContentType.audio;
  if (content.content.contentType?.includes('video'))
    return AlbumContentType.video;
  if (content.content.contentType?.includes('audio'))
    return AlbumContentType.audio;
  if (content.content.contentType?.includes('image'))
    return AlbumContentType.image;
  if (content.content.contentType?.includes('text'))
    return AlbumContentType.write;
  return AlbumContentType.document;
};

const getAlbumRepresentativeIcon = (album: Album) => {
  if (album.contents.length > 0) {
    return ContentIcons[adaptContentType(album.contents[0])];
  }
  return ContentIcons[AlbumContentType.document];
};

export {
  adaptContentType,
  vaultHasPublicClip,
  anyClipInProcess,
  calculateHoursAndMinutes,
  canBuyClips,
  canBuyExtraVideoCallTime,
  canUpgradeVault,
  capitalizeFirstLetter,
  checkEmailAndGetGenericError,
  checkStateAndGetError,
  checkValueAndGetEmptyError,
  classnames,
  convertSecondsToTimeUnit,
  copyToClipboard,
  createDateRange,
  dateTimePassedIsLessThan,
  excludeDefaultAlbum,
  albumDisplayName,
  formatPrice,
  formatProgressBarTime,
  formatRecordingTime,
  getVaultStatusLabel,
  getAllMediaQueries,
  getClipNameOrVaultTitle,
  getClipPriceByVault,
  getDateRange,
  getExtraCallTimeHourPriceVault,
  getIndexClipSelectedInList,
  getInitials,
  getMaxQuestionsAllowedByVault,
  getPermissionsByVault,
  getPriceInfoBySubscription,
  getQueryParam,
  getQueryParamsObject,
  getRoleDisplay,
  getSortedClipsByRecordedAt,
  getAlbumRepresentativeIcon,
  getSubscriptionSchemeById,
  hasVaultAccess,
  hasVaultReachedLimitMessage,
  hasContributor,
  hasNotUserValidatedEmail,
  isAnyLoggedUser,
  isCanceledVault,
  isChromeVersionWithIssues,
  isContributor,
  isEditingVault,
  isEditingMyTemplates,
  isEmailValid,
  isExpiredDate,
  isFreeTrial,
  isGuest,
  isLegacy,
  isLifeTime,
  isLockedVault,
  isLowerBrowserVersion,
  isNotEmptyAndInvalidEmail,
  isOwner,
  isOwnerOrClipOwner,
  isPasswordValid,
  isReadOnly,
  isReadOnlySubOrRestrictedVault,
  isRestrictedVault,
  isSpecificBrowserVersion,
  isTemporaryUser,
  isValidForm,
  noEmptyFields,
  noErrors,
  questionsHaveChanged,
  redictDependingFailingStatus,
  showTextEllipsis,
  sleep,
  totalVaultVideoClips,
  userPermissionUpgrade,
};

export type { HTMLValidationError };
